import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compact, debounce, get } from 'lodash';
import moment from 'moment';
import {
  compose,
  setDisplayName,
} from 'recompose';
import TextField from '@material-ui/core/TextField';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import ArrowIcon from '@material-ui/icons/ArrowRightAlt';
import DollarIcon from '@material-ui/icons/LocalAtm';
import TransferIcon from '@material-ui/icons/AccountBalance';
import { withStyles } from '@material-ui/core/styles';

import {
  injectIntl,
  Divider,
  HeaderTitle,
  HighlightText,
  LightButton,
  SearchComponent,
  SmartTable,
  Tooltip,
} from 'components';
import SelectField from 'components/SmartForm/components/SelectField';
import { payOnlineHelper } from 'constants/strings';
import immutableToJS from 'helpers/immutableToJS';
import {
  toShortDateTimeFormat,
  toCurrency,
  toLongDateFormat,
} from 'helpers/utils';
import creditsActions, { withdrawCredits, purchaseCredits, transferCredits } from 'redux/credits/actions';
import { refreshToken } from 'redux/auth/actions';
import contactsActions from 'redux/contacts/actions';
import { selectState, getRequestingSelector } from 'redux/selectors';

import AutoFill from './AutoFill';
import PaymentInfoButton from './PaymentInfoButton';
import PayoutsButton from './PayoutsButton';

const styles = () => ({
  selectFloatingLabel: { width: '120%' },
  selectLabel: {
    color: '#808080',
  },
  selectRoot: {
    marginRight: 30,
    width: '200px',
  },
});

const actions = [
  { value: '*', label: 'All', isForDropDown: true },
  { value: 'invoice_paid', label: 'Received Payment', isForDropDown: true, payOnline: true },
  { value: 'receive', label: 'Received credits', isForDropDown: true, payOnline: true },
  { value: 'pay_invoice', label: 'Sent payments', isForDropDown: true, payOnline: true },
  { value: 'send', label: 'Sent credits', isForDropDown: true, payOnline: true },
  { value: 'withdraw', label: 'Transfers to bank', isForDropDown: true, payOnline: true },
  { value: 'purchase', label: 'Transfers from bank to balance', isForDropDown: true },
  { value: 'pay_invoice_basic_fee', label: 'Freshr fee', isForDropDown: true },
  { value: 'pay_invoice_card_processing_fee', label: 'Card Processing Fee' },
  { value: 'pay_invoice_ach_processing_fee', label: 'ACH Processing Fee' },
  { value: 'pay_member_fee', label: 'Member Fee' },
  { value: 'pay_monthly_fee', label: 'Monthly Fee' },
  { value: 'refund_invoice', label: 'Invoice Refunded' },
  // { value: 'invoice_basic_fee', label: 'Transaction Fee' },
];

class Payments extends PureComponent {
  static contextTypes = {
    currentUser: PropTypes.shape({}),
    globals: PropTypes.shape({}),
    noAutoPay: PropTypes.bool,
    payOnline: PropTypes.bool,
    isConsumer: PropTypes.bool,
  };

  static propTypes = {
    classes: PropTypes.shape({}).isRequired,
    credits: PropTypes.arrayOf(PropTypes.shape({})),
    creditsRequesting: PropTypes.bool,
    creditRequesting: PropTypes.bool,
    formatMessage: PropTypes.func.isRequired,
    listCredits: PropTypes.func.isRequired,
    withdrawCredits: PropTypes.func.isRequired,
    purchaseCredits: PropTypes.func.isRequired,
    transferCredits: PropTypes.func.isRequired,
    refreshToken: PropTypes.func.isRequired,
  };

  static defaultProps = {
    credits: [],
    creditRequesting: false,
    creditsRequesting: false,
  };

  state = {
    coinsStatusTab: 'available',
    selectedContact: null,
    action: '',
    searchPaymentText: '',
    withdrawAmount: '',
    purchaseAmount: '',
    transferAmount: '',
    transferNotes: '',
  };

  componentWillMount() {
    this.onSearchPayment();
  }

  onChangeCoinStatusTab = (e, coinsStatusTab) => {
    this.setState({ coinsStatusTab }, this.onSearchPayment);
  };

  onSearchPaymentText = (value) => {
    this.setState(
      { searchPaymentText: value },
      debounce(this.onSearchPayment, 500),
    );
  };

  onSearchFilterAction = (value) => {
    this.setState(
      { action: value },
      this.onSearchPayment,
    );
  };

  onCancelSearchPayment = () => {
    this.setState(
      { searchPaymentText: '' },
      this.onSearchPayment,
    );
  }

  onSearchPayment = () => {
    const { listCredits } = this.props;
    const { coinsStatusTab, searchPaymentText, action } = this.state;
    listCredits({
      pending: coinsStatusTab === 'pending',
      search: encodeURIComponent(searchPaymentText),
      action,
    });
  };

  getFields = () => {
    const { formatMessage } = this.props;
    const { coinsStatusTab, searchPaymentText } = this.state;
    return compact([
      coinsStatusTab === 'pending' && ({
        name: 'createdAt',
        label: formatMessage('Time'),
        render: row => (
          toShortDateTimeFormat(row.createdAt)
        ),
      }),
      coinsStatusTab === 'available' && ({
        name: 'availableAt',
        label: formatMessage('Time'),
        render: row => (
          toShortDateTimeFormat(row.availableAt || row.createdAt)
        ),
      }),
      {
        name: 'action',
        render: row =>
          (actions.find(({ value }) => row.action === value) || {}).label || row.action,
        label: formatMessage('Action'),
      },
      {
        name: 'description',
        label: formatMessage('Description'),
        render: row => (
          <HighlightText
            highlight={searchPaymentText}
            text={row.description}
          />
        ),
        colSpan: 4,
      },
      {
        name: 'amount',
        label: formatMessage('Amount'),
        render: row => (row.amount > 0 ? toCurrency(row.amount) : `(${toCurrency(Math.abs(row.amount))})`),
      },
      coinsStatusTab === 'available' && ({
        name: 'balance',
        label: formatMessage('Balance'),
        render: row => toCurrency(row.balance),
      }),
      coinsStatusTab === 'pending' && ({
        name: 'availableAt',
        label: formatMessage('Available'),
        render: row => (
          toShortDateTimeFormat(row.availableAt)
        ),
      }),
    ]);
  };

  clearWithdrawCreditInfo = () => {
    this.setState({ withdrawAmount: '' });
  }

  clearPurchaseCreditInfo = () => {
    this.setState({ purchaseAmount: '' });
  }

  clearTransferCreditInfo = () => {
    this.setState({
      transferAmount: '',
      transferNotes: '',
      selectedContact: null,
    });
  }

  handleSelectContact = (contact) => {
    this.setState({
      selectedContact: contact,
    });
  };

  contactItemEmailRenderer = contact => get(contact, 'profile.email');

  handleWithdrawAmountChange = ({ target: { value } }) => {
    this.setState({ withdrawAmount: value });
  };

  handleWithdrawCredit = () => {
    const { currentUser } = this.context;
    const { withdrawCredits, refreshToken } = this.props;
    const { withdrawAmount } = this.state;
    withdrawCredits({
      amount: withdrawAmount || get(currentUser, 'account.creditBalance'),
      onSuccess: () => {
        this.clearWithdrawCreditInfo();
        refreshToken();
      },
    });
  };

  handlePurchaseAmountChange = ({ target: { value } }) => {
    this.setState({ purchaseAmount: value });
  };

  handlePurchaseCredit = () => {
    const { purchaseCredits, refreshToken } = this.props;
    const { purchaseAmount } = this.state;
    purchaseCredits({
      amount: purchaseAmount,
      onSuccess: () => {
        this.clearPurchaseCreditInfo();
        refreshToken();
      },
    });
  };

  handleTransferAmountChange = ({ target: { value } }) => {
    this.setState({ transferAmount: value });
  };

  handleTransferNotesChange = ({ target: { value } }) => {
    this.setState({ transferNotes: value });
  };

  handleTransferCredit = () => {
    const { refreshToken, transferCredits } = this.props;
    const { selectedContact, transferAmount, transferNotes } = this.state;
    transferCredits({
      amount: transferAmount,
      notes: transferNotes,
      contactId: selectedContact.id,
      onSuccess: () => {
        this.clearTransferCreditInfo();
        refreshToken();
      },
    });
  };

  render() {
    const { currentUser, globals, payOnline, isConsumer } = this.context;
    const {
      formatMessage,
      classes,
      creditsRequesting,
      credits,
      creditRequesting,
    } = this.props;
    const {
      coinsStatusTab,
      selectedContact,
      searchPaymentText,
      withdrawAmount,
      purchaseAmount,
      transferAmount,
      transferNotes,
    } = this.state;

    const paymentDetail = get(currentUser, 'account.paymentDetail');
    const defaultSource = get(paymentDetail, 'default_source');
    const paymentMethod = ((get(paymentDetail, 'sources.data') || []).find(item => item.id === defaultSource) || {}).object;
    const connectedAccount = get(currentUser, 'account.connectedAccount');
    const balance = get(currentUser, 'account.creditBalance');
    const { monthlySubscriptionFee } = globals;
    const creditSpentAt = get(currentUser, 'account.creditSpentAt');
    const fields = this.getFields();
    const purchaseFee = (Number(paymentMethod === 'card' ? globals.feeCard : globals.feeACH)) * 0.01;
    const isOrganization = Boolean(get(currentUser, 'account.currentRoleId'));
    const filterActions = actions
      .filter(action => action.isForDropDown)
      .map(action => ({
        ...action,
        disabled: !payOnline && action.payOnline,
      }));
    const tooltip = payOnline ? undefined : formatMessage(payOnlineHelper);

    return (
      <div className="credit-page">
        <HeaderTitle>
          {formatMessage('Payments')}
        </HeaderTitle>
        {!paymentDetail && <h4>{formatMessage('Please setup payment to purchase credits.')}</h4>}
        {!connectedAccount && <h4>{formatMessage('Please connect payment info to withdraw credits.')}</h4>}
        <Divider />
        <div className="row">
          <SearchComponent
            cancelSearch={this.onCancelSearchPayment}
            placeholder={formatMessage('Search')}
            search={this.onSearchPaymentText}
            searchText={searchPaymentText}
          />
          <h2 className="col">{formatMessage('Payment Info')}</h2>
          <div className="col">
            {formatMessage('Balance')}:
            {` ${toCurrency(balance)}`}
            <br />
            <span style={{ fontSize: 11, color: 'gray' }}>
              {formatMessage('Monthly Subscription')}:
              {` ${toCurrency(monthlySubscriptionFee)}`}
              <br />
              {formatMessage('Next Payment Date')}:
              {` ${toLongDateFormat(moment(creditSpentAt).add(1, 'months'))}`}
            </span>
          </div>
          <div className="col text-right">
            {isOrganization && !isConsumer && <PaymentInfoButton />}
            {isOrganization && isConsumer && <div class="d-md-up-none"><PaymentInfoButton /></div>}
            {isOrganization && <PayoutsButton />}
          </div>
        </div>
        <div className="row payment-actions">
          {connectedAccount && (
            <Tooltip tooltip={tooltip}>
              <section className={`col${payOnline ? '' : ' read-only'}`}>
                <div>
                  <div className="d-flex flex-wrap align-items-center">
                    <TransferIcon style={{ margin: '5 15 5 5' }} />
                    <h3 style={{ color: '#74B36C', marginTop: '0.2rem' }}>
                      {formatMessage('Transfer to your bank')}
                    </h3>
                  </div>
                  <div style={{ color: 'gray', paddingBottom: 5 }}>
                    {formatMessage('Transfer from your FRESHr balance to your bank account.')}
                  </div>
                  <div>
                    {formatMessage('Balance:')} {toCurrency(balance)}
                  </div>
                  <div className="form-group">
                    <label htmlFor="withdraw-amount">
                      {formatMessage('Amount')}:
                    </label>
                    <TextField
                      id="withdraw-amount"
                      onChange={this.handleWithdrawAmountChange}
                      required
                      placeholder=""
                      type="number"
                      value={withdrawAmount}
                      disabled={!payOnline}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end">
                  <LightButton
                    onClick={this.handleWithdrawCredit}
                    disabled={
                      creditRequesting ||
                      !connectedAccount ||
                      !withdrawAmount ||
                      withdrawAmount <= 0
                    }
                  >
                    {formatMessage('Transfer to bank')}
                  </LightButton>
                </div>
              </section>
            </Tooltip>
          )}
          {paymentDetail && (
            <section className="col">
              <div>
                <div className="d-flex flex-wrap align-items-center">
                  <DollarIcon style={{ margin: '5 15 5 5', fontSize: 30 }} />
                  <h3 style={{ color: '#4595EC', marginTop: '0.3rem' }}>
                    {formatMessage('Transfers from bank to balance')}
                  </h3>
                </div>
                <div style={{ color: 'gray', paddingBottom: 5 }}>
                  {formatMessage('Use it to shop on FRESHr, or save it for a rainy day.')}
                </div>
                <div>
                  {formatMessage('Payment method:')} {paymentMethod},{' '}
                  {formatMessage('Fee:')} {(purchaseFee * 100).toFixed(1)}%
                </div>
                <div className="form-group">
                  <label htmlFor="purchase-amount">
                    {formatMessage('Amount')}:
                  </label>
                  <TextField
                    id="purchase-amount"
                    onChange={this.handlePurchaseAmountChange}
                    required
                    type="number"
                    value={purchaseAmount}
                  />
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  {formatMessage('You will spend')} ${(purchaseAmount * (1 + purchaseFee)).toFixed(2)}
                </div>
                <LightButton
                  onClick={this.handlePurchaseCredit}
                  disabled={creditRequesting || !paymentDetail || !(purchaseAmount > 0)}
                >
                  {formatMessage('Add to balance')}
                </LightButton>
              </div>
            </section>
          )}
          <Tooltip tooltip={tooltip}>
            <section className={`col${payOnline ? '' : ' read-only'}`}>
              <div>
                <div className="d-flex flex-wrap align-items-center">
                  <ArrowIcon style={{ margin: '5 15 5 5', fontSize: 30 }} />
                  <h3 style={{ color: '#EE6336', marginTop: '0.3rem' }}>
                    {formatMessage('Send credit to a FRESHr user')}
                  </h3>
                </div>
                <div style={{ color: 'gray', paddingBottom: 5 }}>
                  {formatMessage('Use it to transfer credit or a refund to another user.')}
                </div>
                <div>
                  {formatMessage('Balance:')} {toCurrency(balance)}
                </div>
                <div className="row">
                  <div className="form-group col">
                    <label htmlFor="transfer-amount">
                      {formatMessage('Amount')}:
                    </label>
                    <span style={{ paddingRight: 5 }}>$</span>
                    <TextField
                      id="transfer-amount"
                      onChange={this.handleTransferAmountChange}
                      required
                      type="number"
                      value={transferAmount}
                      disabled={!payOnline}
                    />
                  </div>
                  <div className="form-group col">
                    <label htmlFor="transfer-notes">
                      {formatMessage('Notes')}:
                    </label>
                    <TextField
                      id="transfer-notes"
                      onChange={this.handleTransferNotesChange}
                      value={transferNotes}
                      disabled={!payOnline}
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col d-flex align-items-center">
                  <label className="mr-2">
                    {formatMessage('To')}:
                  </label>
                  <AutoFill
                    onSelect={this.handleSelectContact}
                    disabled={!payOnline}
                  />
                </div>
                <div style={{ marginRight: 15 }}>
                  <LightButton
                    onClick={this.handleTransferCredit}
                    disabled={creditRequesting || !paymentDetail || !(transferAmount > 0) ||
                      !selectedContact || !selectedContact.id}
                  >
                    {formatMessage('Transfer to user')}
                  </LightButton>
                </div>
              </div>
            </section>
          </Tooltip>
        </div>
        <Divider />
        <div className={`loading ${creditsRequesting ? 'loading-enabled' : ''}`}>
          <div className="d-flex">
            <SelectField
              classes={{
                root: { root: classes.selectRoot },
                label: { root: classes.selectFloatingLabel },
                select: { select: classes.selectLabel },
              }}
              defaultValue="*"
              label={formatMessage('Filter Action')}
              name="filter-action"
              options={filterActions}
              onChange={(e) => {
                this.onSearchFilterAction(e === '*' ? '' : e);
              }}
            />
            <Tabs
              className="credits-type-tabs"
              onChange={this.onChangeCoinStatusTab}
              value={coinsStatusTab}
              textColor="primary"
            >
              <Tab
                label={formatMessage('Pending')}
                value="pending"
              />
              <Tab
                label={formatMessage('Available')}
                value="available"
              />
            </Tabs>
          </div>
          {!creditsRequesting && (
            <SmartTable
              className="credits-table"
              data={credits.filter(({ pending }) => (coinsStatusTab === 'pending' ? pending : !pending))}
              fields={fields}
              threshold="md"
            />
          )}
        </div>
      </div>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = state => ({
  ...selectState('credits', 'credits')(state),
  ...selectState('contacts', 'contacts')(state),
  creditRequesting: getRequestingSelector('credits', 'credit')(state, 'creditRequesting'),
});

/* istanbul ignore next */
const mapDispatchToProps = dispatch => ({
  listContacts: payload => dispatch(contactsActions.list(payload)),
  listCredits: payload => dispatch(creditsActions.list(payload)),
  withdrawCredits: payload => dispatch(withdrawCredits(payload)),
  purchaseCredits: payload => dispatch(purchaseCredits(payload)),
  transferCredits: payload => dispatch(transferCredits(payload)),
  refreshToken: () => dispatch(refreshToken()),
});

export default compose(
  setDisplayName('Payments'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  immutableToJS,
  injectIntl,
  withStyles(styles),
)(Payments);
